// apiService.js - Service API pour la PWA mobile (avec cloneManager)

import cloneManager from './cloneManager';

/** Aligne la puissance fiscale (texte libre) sur les clés barème 3cv … 7cv_et_plus */
export function normalizePuissanceFiscaleKey(raw) {
  if (raw == null || raw === '') return '5cv';
  const s = String(raw)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s/g, '');
  if (s.includes('7cv') || /^7[^0-9]/.test(s)) return '7cv_et_plus';
  const m = s.match(/(\d)\s*cv/);
  if (m) {
    const n = m[1];
    if (n === '3') return '3cv';
    if (n === '4') return '4cv';
    if (n === '5') return '5cv';
    if (n === '6') return '6cv';
    if (parseInt(n, 10) >= 7) return '7cv_et_plus';
  }
  return '5cv';
}

function mapInspecteurToVehiculeUi(inspector) {
  if (!inspector) return null;
  return {
    marque: inspector.vehicule_marque || '',
    modele: inspector.vehicule_modele || '',
    immatriculation: inspector.vehicule_immatriculation || '',
    puissance_fiscale: normalizePuissanceFiscaleKey(inspector.vehicule_puissance_fiscale),
    kilometrage_annuel_estime: inspector.vehicule_kilometrage_annuel_estime ?? 0,
    kilometrage_utilise_annuel: inspector.vehicule_kilometrage_utilise_annuel ?? 0,
  };
}

class ApiService {
  constructor() {
    // Plus besoin de stocker token et apiUrl ici
    // On utilise cloneManager directement
  }

  getApiUrl() {
    const url = cloneManager.getApiUrl();

    if (!url) {
      console.warn('⚠️ Aucune URL API configurée (clone non connecté)');
      return null;
    }

    // Les routes Rapteck sont sous /api/... (ex. /api/integrations/llm). Connexion manuelle ou anciennes configs peuvent omettre /api.
    let normalized = String(url).trim().replace(/\/+$/, '');
    if (!/\/api$/i.test(normalized)) {
      normalized = `${normalized}/api`;
    }
    return normalized;
  }

  getToken() {
    const token = cloneManager.getToken();
    
    if (!token) {
      console.warn('⚠️ Aucun token disponible (non authentifié)');
      return null;
    }
    
    return token;
  }

  getAuthHeaders() {
    const token = this.getToken();
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    };
  }

  getAuthHeadersForFormData() {
    const token = this.getToken();
    return {
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    };
  }

  // Helper pour les appels API
  async callFunction(functionName, payload = {}) {
    try {
      const baseUrl = this.getApiUrl();
      
      if (!baseUrl) {
        console.error('❌ Pas d\'URL API configurée');
        return { success: false, error: 'Non authentifié' };
      }
      
      const url = `${baseUrl}/functions/${functionName}`;
      
      console.log('🌐 callFunction - URL:', url);
      console.log('🌐 callFunction - Payload:', JSON.stringify(payload, null, 2));
      
      const response = await fetch(url, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(payload)
      });

      console.log('🌐 callFunction - Status:', response.status);
      console.log('🌐 callFunction - StatusText:', response.statusText);

      if (!response.ok) {
        console.error(`❌ Erreur HTTP ${functionName}:`, response.status);
        
        // Essayer de lire le corps de la réponse pour avoir plus de détails
        try {
          const errorText = await response.text();
          console.error('❌ Corps de l\'erreur:', errorText);
          return { success: false, error: `HTTP ${response.status}: ${errorText}` };
        } catch (e) {
          return { success: false, error: `HTTP ${response.status}` };
        }
      }

      const data = await response.json();
      console.log('🌐 callFunction - Data:', JSON.stringify(data, null, 2));
      return data;
    } catch (error) {
      console.error(`❌ Erreur ${functionName}:`, error);
      console.error('❌ Stack:', error.stack);
      return { success: false, error: error.message };
    }
  }

  // 🔥 Récupérer la liste des inspecteurs
  async getInspecteurs() {
    try {
      const baseUrl = this.getApiUrl();
      const token = this.getToken();
      
      if (!baseUrl || !token) {
        console.warn('⚠️ Pas de token pour getInspecteurs');
        return [];
      }

      const url = `${baseUrl}/inspecteurs`;
      console.log('📥 Chargement inspecteurs:', url);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        console.error('❌ Erreur getInspecteurs:', response.status);
        return [];
      }

      const result = await response.json();
      console.log('📦 Inspecteurs chargés:', result.data?.length || 0);
      return result.data || [];
    } catch (error) {
      console.error('❌ Erreur getInspecteurs:', error);
      return [];
    }
  }

  // 🔥 Synchroniser les données d'une mission vers Rapteck
  async syncMissionData(missionId, data) {
    try {
      const baseUrl = this.getApiUrl();
      const token = this.getToken();
      
      if (!baseUrl || !token) {
        console.warn('⚠️ Pas de token ou URL pour sync');
        return { success: false, error: 'Non authentifié' };
      }

      // 🛡️ Strip base64 + pending_photos avant l'envoi (evite HTTP 413).
      // Import dynamique pour eviter une dependance circulaire syncService <-> apiService.
      let payloadData = data;
      try {
        const mod = await import('./syncService');
        if (mod && typeof mod.sanitizeStepsForSync === 'function') {
          const { payload, before, after } = mod.sanitizeStepsForSync(data || {});
          payloadData = payload;
          const beforeMB = (before / 1024 / 1024).toFixed(2);
          const afterMB = (after / 1024 / 1024).toFixed(2);
          if (before !== after) {
            console.log(`📦 Sync payload sanitized: ${beforeMB} MB → ${afterMB} MB`);
          }
        }
      } catch (sanitizeErr) {
        console.warn('⚠️ Sanitize sync payload failed, envoi brut:', sanitizeErr?.message || sanitizeErr);
      }

      const url = `${baseUrl}/mobile/missions/${missionId}/sync`;
      console.log('🔄 Sync mission vers:', url);

      const response = await fetch(url, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          statut: payloadData.statut,
          observations: payloadData.observations,
          date_realisee: payloadData.date_realisee,
          rapport_data: payloadData
        })
      });

      if (!response.ok) {
        console.error('❌ Erreur sync:', response.status);
        let errMsg = `HTTP ${response.status}`;
        try {
          const errJson = await response.json();
          if (errJson.message) errMsg = errJson.message;
        } catch (_) {}
        return { success: false, error: errMsg, status: response.status };
      }

      const result = await response.json();
      console.log('✅ Sync réussie:', result);
      return result;
    } catch (error) {
      console.error('❌ Erreur syncMissionData:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Pose / renouvelle le verrou pessimiste d'un rapport.
   * POST /api/rapports/:id/lock  body { side: 'pwa' | 'web' }
   * Renvoie { success, data: { lockedBy, lockedByInspecteurId, lockedAt, lockedByName } }
   * En cas de conflit (rapport déjà verrouillé par un autre) : success=false, status=409, data avec détails.
   */
  async lockRapport(rapportId, side = 'pwa') {
    try {
      const baseUrl = this.getApiUrl();
      const token = this.getToken();
      if (!baseUrl || !token) return { success: false, error: 'Non authentifié' };
      if (!rapportId) return { success: false, error: 'Rapport inconnu' };

      const url = `${baseUrl}/rapports/${encodeURIComponent(rapportId)}/lock`;
      const response = await fetch(url, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ side }),
      });
      let payload = {};
      try { payload = await response.json(); } catch (_) {}
      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          code: payload.code || null,
          error: payload.message || `HTTP ${response.status}`,
          data: payload.data || null,
        };
      }
      return { success: true, data: payload.data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Libère le verrou d'un rapport (propriétaire uniquement).
   * POST /api/rapports/:id/unlock
   */
  async unlockRapport(rapportId) {
    try {
      const baseUrl = this.getApiUrl();
      const token = this.getToken();
      if (!baseUrl || !token) return { success: false, error: 'Non authentifié' };
      if (!rapportId) return { success: false, error: 'Rapport inconnu' };

      const url = `${baseUrl}/rapports/${encodeURIComponent(rapportId)}/unlock`;
      const response = await fetch(url, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({}),
      });
      let payload = {};
      try { payload = await response.json(); } catch (_) {}
      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          code: payload.code || null,
          error: payload.message || `HTTP ${response.status}`,
        };
      }
      return { success: true, data: payload.data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * État courant du verrou d'un rapport.
   * GET /api/rapports/:id/lock-status
   */
  async getRapportLockStatus(rapportId) {
    try {
      const baseUrl = this.getApiUrl();
      const token = this.getToken();
      if (!baseUrl || !token) return { success: false, error: 'Non authentifié' };
      if (!rapportId) return { success: false, error: 'Rapport inconnu' };
      const url = `${baseUrl}/rapports/${encodeURIComponent(rapportId)}/lock-status`;
      const response = await fetch(url, { method: 'GET', headers: this.getAuthHeaders() });
      let payload = {};
      try { payload = await response.json(); } catch (_) {}
      if (!response.ok) {
        return { success: false, status: response.status, error: payload.message || `HTTP ${response.status}` };
      }
      return { success: true, data: payload.data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Valider un rapport (attribution numéro chrono côté API).
   * POST /api/rapports/:id/valider
   */
  async validateRapport(rapportId) {
    try {
      const baseUrl = this.getApiUrl();
      const token = this.getToken();

      if (!baseUrl || !token) {
        return { success: false, error: 'Non authentifié' };
      }
      if (!rapportId) {
        return { success: false, error: 'Rapport inconnu' };
      }

      const url = `${baseUrl}/rapports/${encodeURIComponent(rapportId)}/valider`;
      const response = await fetch(url, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({}),
      });

      let payload = {};
      try {
        payload = await response.json();
      } catch (_) {
        payload = {};
      }

      if (!response.ok) {
        const msg =
          payload.message ||
          payload.error ||
          `HTTP ${response.status}`;
        return { success: false, error: msg, status: response.status };
      }

      return { success: true, data: payload.data };
    } catch (error) {
      console.error('❌ Erreur validateRapport:', error);
      return { success: false, error: error.message };
    }
  }

  // 🔥 Récupérer les détails d'une mission
  async getMissionDetails(missionId) {
    try {
      const baseUrl = this.getApiUrl();
      const token = this.getToken();
      
      if (!baseUrl || !token) {
        return { success: false, error: 'Non authentifié' };
      }

      const url = `${baseUrl}/mobile/missions/${missionId}`;
      console.log('📥 Chargement mission:', url);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        console.error('❌ Erreur getMissionDetails:', response.status);
        return { success: false, error: `HTTP ${response.status}` };
      }

      const result = await response.json();
      console.log('📦 Mission chargée:', result);
      return result;
    } catch (error) {
      console.error('❌ Erreur getMissionDetails:', error);
      return { success: false, error: error.message };
    }
  }

  // 🔥 Récupérer les missions de l'inspecteur
  async getMissions() {
    try {
      const baseUrl = this.getApiUrl();
      const token = this.getToken();
      
      if (!baseUrl || !token) {
        console.warn('⚠️ Pas de token ou URL pour getMissions');
        return [];
      }

      // ✅ Laisser l’API filtrer via le token (inspecteurId du JWT)
      // Évite les incohérences si l’ID inspecteur local est obsolète.
      const url = `${baseUrl}/mobile/missions`;
      console.log('📥 Chargement missions:', url);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        let errorText = '';
        try {
          errorText = await response.text();
        } catch (e) {
          errorText = '';
        }
        console.error('❌ Erreur getMissions:', response.status, errorText);
        return [];
      }

      const result = await response.json();
      const missions = Array.isArray(result.data) ? result.data : [];
      const normalized = missions.map((mission) => {
        const safeString = (value) => (typeof value === 'string' ? value : '');
        const rawType = safeString(mission.activite) || safeString(mission.type_mission) || safeString(mission.typeMission);
        const lowerType = rawType.toLowerCase();
        const activite = lowerType.includes('levage') || lowerType.includes('vgp')
          ? 'levage'
          : lowerType.includes('thermo')
            ? 'thermographie'
            : lowerType.includes('incendie')
              ? 'incendie'
              : 'electricite';
        const clientNom =
          safeString(mission.client_nom) ||
          safeString(mission.client?.entreprise) ||
          safeString(`${mission.client?.nom || ''} ${mission.client?.prenom || ''}`.trim());
        const etablissement =
          safeString(mission.etablissement) ||
          safeString(mission.etablissement_data?.nom) ||
          safeString(mission.etablissement?.nom);
        const siteAdresse =
          safeString(mission.site_adresse) ||
          safeString(mission.etablissement_data?.adresse) ||
          safeString(mission.etablissement?.adresse);
        const numeroRapport =
          safeString(mission.numero_rapport) ||
          `Mission ${mission.type_mission || mission.typeMission || mission.id}`;
        const statusMap = {
          planifiee: 'a_venir',
          creee: 'a_venir',
          en_cours: 'en_cours',
          terminee: 'completee',
          completee: 'completee',
          annulee: 'annulee',
        };
        const rawStatus = safeString(mission.statut) || safeString(mission.statut_mobile);
        const statut_mobile = statusMap[rawStatus] || rawStatus || 'a_venir';

        const dateStart = mission.date_prevue || mission.datePrevue || mission.date_affectation;
        const dureeMinutes = Number.isFinite(mission.duree_minutes) && mission.duree_minutes > 0
          ? mission.duree_minutes
          : null;
        const padTwo = (n) => String(n).padStart(2, '0');
        const computeHHmm = (iso, addMinutes = 0) => {
          if (!iso) return null;
          const d = new Date(iso);
          if (Number.isNaN(d.getTime())) return null;
          const adjusted = addMinutes ? new Date(d.getTime() + addMinutes * 60000) : d;
          return `${padTwo(adjusted.getHours())}:${padTwo(adjusted.getMinutes())}`;
        };
        const heureDebut = mission.heure_debut || computeHHmm(dateStart);
        const heureFin = mission.heure_fin
          || computeHHmm(dateStart, dureeMinutes || 180);

        return {
          ...mission,
          activite,
          type_verification: safeString(mission.type_verification) || rawType || activite,
          client_nom: clientNom,
          etablissement,
          site_adresse: siteAdresse,
          numero_rapport: numeroRapport,
          statut_mobile,
          heure_debut: heureDebut,
          heure_fin: heureFin,
          duree_minutes: dureeMinutes,
          inspecteur_couleur_calendrier: mission.inspecteur_couleur_calendrier || mission.inspecteur?.couleurCalendrier || null,
          date_affectation: mission.date_affectation || mission.date_prevue || mission.datePrevue || null,
          date_inspection: mission.date_inspection || mission.date_prevue || mission.datePrevue || null,
        };
      });
      console.log('📦 Missions chargées:', normalized.length);
      return normalized;
    } catch (error) {
      console.error('❌ Erreur getMissions:', error);
      return [];
    }
  }

  // 🔥 Créer une mission depuis la PWA (nouveau rapport sans mission planifiée)
  async createMission(payload) {
    try {
      const baseUrl = this.getApiUrl();
      const token = this.getToken();
      if (!baseUrl || !token) {
        return { success: false, error: 'Non authentifié' };
      }
      const url = `${baseUrl}/mobile/missions`;
      const response = await fetch(url, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          etablissement_id: payload.etablissement_id,
          type_mission: payload.type_mission,
          date_prevue: payload.date_prevue,
        }),
      });
      if (!response.ok) {
        const errText = await response.text();
        let msg = `HTTP ${response.status}`;
        try {
          const errJson = JSON.parse(errText);
          if (errJson.message) msg = errJson.message;
        } catch (_) {}
        return { success: false, error: msg };
      }
      const result = await response.json();
      const mission = result.data;
      if (!mission) return { success: false, error: 'Réponse invalide' };
      const safeString = (value) => (typeof value === 'string' ? value : '');
      const rawType = safeString(mission.activite) || safeString(mission.type_mission) || safeString(mission.typeMission);
      const lowerType = rawType.toLowerCase();
      const activite = lowerType.includes('levage') || lowerType.includes('vgp')
        ? 'levage'
        : lowerType.includes('thermo')
          ? 'thermographie'
          : lowerType.includes('incendie')
            ? 'incendie'
            : 'electricite';
      const clientNom =
        safeString(mission.client_nom) ||
        safeString(mission.client?.entreprise) ||
        safeString(`${mission.client?.nom || ''} ${mission.client?.prenom || ''}`.trim());
      const etablissement =
        safeString(mission.etablissement) ||
        safeString(mission.etablissement_data?.nom) ||
        safeString(mission.etablissement?.nom);
      const siteAdresse =
        safeString(mission.site_adresse) ||
        safeString(mission.etablissement_data?.adresse) ||
        safeString(mission.etablissement?.adresse);
      const numeroRapport =
        safeString(mission.numero_rapport) ||
        `Mission ${mission.type_mission || mission.typeMission || mission.id}`;
      const statusMap = {
        planifiee: 'a_venir',
        creee: 'a_venir',
        en_cours: 'en_cours',
        terminee: 'completee',
        completee: 'completee',
        annulee: 'annulee',
      };
      const rawStatus = safeString(mission.statut) || safeString(mission.statut_mobile);
      const statut_mobile = statusMap[rawStatus] || rawStatus || 'a_venir';
      const normalized = {
        ...mission,
        activite,
        type_verification: safeString(mission.type_verification) || rawType || activite,
        client_nom: clientNom,
        etablissement,
        site_adresse: siteAdresse,
        numero_rapport: numeroRapport,
        statut_mobile,
        date_affectation: mission.date_affectation || mission.date_prevue || mission.datePrevue || null,
        date_inspection: mission.date_inspection || mission.date_prevue || mission.datePrevue || null,
      };
      return { success: true, data: normalized };
    } catch (error) {
      console.error('❌ Erreur createMission:', error);
      return { success: false, error: error.message };
    }
  }

  // ✅ Récupérer les templates observations levage
  async getTemplatesLevage() {
    try {
      const baseUrl = this.getApiUrl();
      const token = this.getToken();

      if (!baseUrl || !token) {
        return { success: false, error: 'Token manquant', templates: [] };
      }

      const url = `${baseUrl}/referentiels/templates-observations-levage`;
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        return { success: false, error: `HTTP ${response.status}`, templates: [] };
      }

      const result = await response.json();
      return { success: true, templates: result.data || [] };
    } catch (error) {
      return { success: false, error: error.message, templates: [] };
    }
  }

  // ✅ Récupérer les grilles de points de contrôle levage
  async getGrillesPointsControleLevage() {
    try {
      const baseUrl = this.getApiUrl();
      const token = this.getToken();

      if (!baseUrl || !token) {
        return { success: false, error: 'Token manquant', grilles: {} };
      }

      const [sectionsRes, pointsRes] = await Promise.all([
        fetch(`${baseUrl}/referentiels/sections-levage`, { method: 'GET', headers: this.getAuthHeaders() }),
        fetch(`${baseUrl}/referentiels/points-controle-levage`, { method: 'GET', headers: this.getAuthHeaders() }),
      ]);

      if (!sectionsRes.ok) {
        return { success: false, error: `HTTP ${sectionsRes.status}`, grilles: {} };
      }
      if (!pointsRes.ok) {
        return { success: false, error: `HTTP ${pointsRes.status}`, grilles: {} };
      }

      const sectionsPayload = await sectionsRes.json();
      const pointsPayload = await pointsRes.json();

      if (!sectionsPayload?.success || !pointsPayload?.success) {
        return { success: false, error: 'Erreur API', grilles: {} };
      }

      const sections = sectionsPayload.data || [];
      const points = pointsPayload.data || [];

      if (!sections.length) {
        return { success: false, error: 'Aucune section levage disponible', grilles: {} };
      }

      const pointsBySection = new Map();
      points.forEach((point) => {
        if (!pointsBySection.has(point.section_id)) {
          pointsBySection.set(point.section_id, []);
        }
        pointsBySection.get(point.section_id).push(point);
      });

      const colorPalette = [
        'bg-blue-600',
        'bg-green-600',
        'bg-purple-600',
        'bg-orange-600',
        'bg-red-600',
        'bg-yellow-600',
        'bg-teal-600',
        'bg-indigo-600',
        'bg-pink-600',
        'bg-cyan-600',
        'bg-amber-600',
        'bg-slate-600',
      ];

      const sectionsGrid = sections
        .slice()
        .sort((a, b) => (a.ordre ?? 0) - (b.ordre ?? 0))
        .map((section, idx) => {
          const sectionPoints = (pointsBySection.get(section.id) || [])
            .slice()
            .sort((a, b) => (a.ordre ?? 0) - (b.ordre ?? 0));

          return {
            titre: section.libelle || section.nom || section.code,
            couleur: colorPalette[idx % colorPalette.length],
            points: sectionPoints.map((p) => ({
              num: p.code,
              libelle: p.libelle,
            })),
          };
        });

      const famillesCodes = [
        'B1', 'B2', 'B3', 'B4', 'B5', 'B6', 'B7', 'B8', 'B9', 'B10', 'B11', 'C1', 'D1',
      ];
      const grilles = {};
      famillesCodes.forEach((code) => {
        grilles[code] = sectionsGrid;
      });

      return { success: true, grilles, count: Object.keys(grilles).length };
    } catch (error) {
      return { success: false, error: error.message, grilles: {} };
    }
  }

  // ✅ Récupérer le référentiel UTE C15-103 (locaux)
  async getReferentielLocaux() {
    try {
      const baseUrl = this.getApiUrl();
      const token = this.getToken();

      if (!baseUrl || !token) {
        return { success: false, error: 'Token manquant', referentiels: [] };
      }

      const url = `${baseUrl}/referentiels/referentiels-locaux`;
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        return { success: false, error: `HTTP ${response.status}`, referentiels: [] };
      }

      const result = await response.json();
      return { success: true, referentiels: result.data || [] };
    } catch (error) {
      return { success: false, error: error.message, referentiels: [] };
    }
  }

  // ✅ Récupérer la bibliothèque d'appareils de référence
  async getAppareilsReference() {
    try {
      const baseUrl = this.getApiUrl();
      const token = this.getToken();

      if (!baseUrl || !token) {
        return { success: false, error: 'Token manquant', appareils: [] };
      }

      const url = `${baseUrl}/referentiels/appareils-reference`;
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        return { success: false, error: `HTTP ${response.status}`, appareils: [] };
      }

      const result = await response.json();
      return { success: true, appareils: result.data || [] };
    } catch (error) {
      return { success: false, error: error.message, appareils: [] };
    }
  }

  // ✅ Bibliothèque modèles appareils (levage)
  async getModelesAppareils() {
    try {
      const baseUrl = this.getApiUrl();
      const token = this.getToken();

      if (!baseUrl || !token) {
        return { success: false, error: 'Token manquant', modeles: [] };
      }

      const url = `${baseUrl}/referentiels/modeles-appareils`;
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        return { success: false, error: `HTTP ${response.status}`, modeles: [] };
      }

      const result = await response.json();
      return { success: true, modeles: result.data || [] };
    } catch (error) {
      return { success: false, error: error.message, modeles: [] };
    }
  }

  // ✅ Familles machines (levage)
  async getFamillesMachines() {
    try {
      const baseUrl = this.getApiUrl();
      const token = this.getToken();

      if (!baseUrl || !token) {
        return { success: false, error: 'Token manquant', familles: [] };
      }

      const url = `${baseUrl}/referentiels/familles-machines`;
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        return { success: false, error: `HTTP ${response.status}`, familles: [] };
      }

      const result = await response.json();
      return { success: true, familles: result.data || [] };
    } catch (error) {
      return { success: false, error: error.message, familles: [] };
    }
  }

  // ✅ Désignations (domaines machines) filtrées par famille
  async getDomainesMachinesByFamille(familleId) {
    try {
      const baseUrl = this.getApiUrl();
      const token = this.getToken();

      if (!baseUrl || !token) {
        return { success: false, error: 'Token manquant', domaines: [] };
      }

      const url = `${baseUrl}/referentiels/domaines-machines?family_id=${encodeURIComponent(familleId)}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });

      if (!response.ok) {
        return { success: false, error: `HTTP ${response.status}`, domaines: [] };
      }

      const result = await response.json();
      return { success: true, domaines: result.data || [] };
    } catch (error) {
      return { success: false, error: error.message, domaines: [] };
    }
  }

  // ✅ Créer une désignation (domaine machine)
  async createDomaineMachine(payload) {
    try {
      const baseUrl = this.getApiUrl();
      const token = this.getToken();

      if (!baseUrl || !token) {
        return { success: false, error: 'Token manquant' };
      }

      const url = `${baseUrl}/referentiels/domaines-machines`;
      const response = await fetch(url, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(payload || {}),
      });

      if (!response.ok) {
        const errorText = await response.text();
        return { success: false, error: errorText || `HTTP ${response.status}` };
      }

      const result = await response.json();
      return { success: true, domaine: result.data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // ✅ Upload fichier (image/PDF) pour import
  async uploadFile(file, type = 'document') {
    try {
      const baseUrl = this.getApiUrl();
      const token = this.getToken();

      if (!baseUrl || !token) {
        return { success: false, error: 'Token manquant' };
      }

      const url = `${baseUrl}/parametres/upload-file?type=${encodeURIComponent(type)}`;
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(url, {
        method: 'POST',
        headers: this.getAuthHeadersForFormData(),
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        return { success: false, error: errorText || `HTTP ${response.status}` };
      }

      const result = await response.json();
      return { success: true, file_url: result?.data?.file_url };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // ✅ Appel LLM (Ollama)
  async invokeLlm(payload) {
    try {
      const baseUrl = this.getApiUrl();
      const token = this.getToken();

      if (!baseUrl || !token) {
        return { success: false, error: 'Token manquant' };
      }

      const url = `${baseUrl}/integrations/llm`;
      const response = await fetch(url, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        return { success: false, error: errorText || `HTTP ${response.status}` };
      }

      const result = await response.json();
      if (result && result.success === false) {
        return {
          success: false,
          error: result.message || result.error || 'Erreur reconnaissance',
        };
      }
      const data = result?.data !== undefined ? result.data : result;
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // ✅ Analyse armoire (détection format + extraction)
  async analyzeArmoireImage(file_url) {
    try {
      const baseUrl = this.getApiUrl();
      const token = this.getToken();

      if (!baseUrl || !token) {
        return { success: false, error: 'Token manquant' };
      }

      const url = `${baseUrl}/integrations/analyze-armoire`;
      const response = await fetch(url, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ file_url }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        return { success: false, error: errorText || `HTTP ${response.status}` };
      }

      const result = await response.json();
      return result?.success
        ? { success: true, data: result.data, detection: result.detection }
        : { success: false, error: result?.error || 'Erreur analyse armoire' };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // ✅ Analyse localisations (détection format + extraction)
  async analyzeLocalisationsImage(file_url) {
    try {
      const baseUrl = this.getApiUrl();
      const token = this.getToken();

      if (!baseUrl || !token) {
        return { success: false, error: 'Token manquant' };
      }

      const url = `${baseUrl}/integrations/analyze-localisations`;
      const response = await fetch(url, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ file_url }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        return { success: false, error: errorText || `HTTP ${response.status}` };
      }

      const result = await response.json();
      return result?.success
        ? { success: true, data: result.data, detection: result.detection }
        : { success: false, error: result?.error || 'Erreur analyse localisations' };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Upload d'un justificatif (ticket photo/PDF) vers le serveur.
   * POST /notes-de-frais/upload — renvoie data.file_url (chemin relatif /uploads/...)
   */
  async uploadNoteDeFraisTicket(file) {
    try {
      const baseUrl = this.getApiUrl();
      const token = this.getToken();

      if (!baseUrl || !token) {
        return { success: false, error: 'Token manquant' };
      }
      if (!file) {
        return { success: false, error: 'Fichier manquant' };
      }

      const url = `${baseUrl}/notes-de-frais/upload`;
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(url, {
        method: 'POST',
        headers: this.getAuthHeadersForFormData(),
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        return { success: false, error: errorText || `HTTP ${response.status}` };
      }

      const result = await response.json();
      const fileUrl = result?.data?.file_url;
      if (!fileUrl) {
        return { success: false, error: 'Réponse serveur sans file_url' };
      }
      return { success: true, file_url: fileUrl };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // ⏳ TODO: Créer une note de frais (à migrer)
  async createNoteDeFrais(noteData) {
    try {
      const baseUrl = this.getApiUrl();
      const token = this.getToken();

      if (!baseUrl || !token) {
        return { success: false, error: 'Token manquant' };
      }

      const url = `${baseUrl}/notes-de-frais`;
      const response = await fetch(url, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(noteData),
      });

      if (!response.ok) {
        return { success: false, error: `HTTP ${response.status}` };
      }

      const result = await response.json();
      return {
        success: true,
        note: result.data,
        kilometrage_utilise_annuel: result.kilometrage_utilise_annuel ?? null,
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  /** Itinéraire routier (Google Directions côté serveur — GOOGLE_MAPS_API_KEY) */
  async calculateDistance(origin, destination) {
    try {
      const baseUrl = this.getApiUrl();
      const token = this.getToken();
      if (!baseUrl || !token) {
        return { success: false, error: 'Token ou URL API manquant' };
      }
      const url = `${baseUrl}/integrations/calculate-distance`;
      const response = await fetch(url, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({ origin, destination }),
      });
      let result = {};
      try {
        result = await response.json();
      } catch {
        result = {};
      }
      if (!response.ok) {
        return {
          success: false,
          error: result.message || result.error || `HTTP ${response.status}`,
        };
      }
      const payload = result.data != null ? result.data : result;
      if (payload.success === false) {
        return { success: false, error: payload.error || 'Itinéraire impossible' };
      }
      return {
        success: true,
        distance_km: payload.distance_km,
        distance_text: payload.distance_text,
        duree_text: payload.duree_text,
        destination: payload.destination,
      };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // 🏢 Récupérer la liste des clients pour notes de frais
  async getClientsForFrais() {
    try {
      const baseUrl = this.getApiUrl();
      const token = this.getToken();
      
      if (!baseUrl || !token) {
        console.error('❌ Pas de token pour getClientsForFrais');
        return { success: false, error: 'Token manquant', clients: [] };
      }

      const url = `${baseUrl}/clients`;
      console.log('📥 Chargement clients:', url);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        console.error('❌ Erreur getClientsForFrais:', response.status);
        return { success: false, error: `HTTP ${response.status}`, clients: [] };
      }

      const result = await response.json();
      console.log('📦 Clients chargés:', result.data?.length || 0);
      return { success: true, clients: result.data || [] };
    } catch (error) {
      console.error('❌ Erreur getClientsForFrais:', error);
      return { success: false, error: error.message, clients: [] };
    }
  }

  // ========================================
  // 🚗 NOUVELLES MÉTHODES KILOMÉTRAGE
  // ========================================

  // 🚗 Récupérer les infos véhicule de l'inspecteur
  async getInspecteurVehiculeInfo(inspecteurId) {
    try {
      const baseUrl = this.getApiUrl();
      const token = this.getToken();
      
      if (!baseUrl || !token) {
        console.error('❌ Pas de token pour getInspecteurVehiculeInfo');
        return { success: false, error: 'Token manquant' };
      }

      const url = `${baseUrl}/inspecteurs/${inspecteurId}`;
      console.log('🚗 Chargement infos véhicule:', url);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        console.error('❌ Erreur getInspecteurVehiculeInfo:', response.status);
        return { success: false, error: `HTTP ${response.status}` };
      }

      const result = await response.json();
      const insp = result.data;
      console.log('📦 Infos véhicule chargées:', result);
      return {
        success: true,
        inspecteur: insp,
        vehicule: mapInspecteurToVehiculeUi(insp),
      };
    } catch (error) {
      console.error('❌ Erreur getInspecteurVehiculeInfo:', error);
      return { success: false, error: error.message };
    }
  }

  /** Barèmes IK (GET /baremes-kilometriques) — query: annee, actif, puissance_vehicule */
  async listBaremesKilometriques(params = {}) {
    try {
      const baseUrl = this.getApiUrl();
      const token = this.getToken();
      if (!baseUrl || !token) {
        return { success: false, error: 'Token manquant', baremes: [] };
      }
      const q = new URLSearchParams(
        Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''))
      ).toString();
      const url = `${baseUrl}/baremes-kilometriques${q ? `?${q}` : ''}`;
      const response = await fetch(url, { method: 'GET', headers: this.getAuthHeaders() });
      if (!response.ok) {
        return { success: false, error: `HTTP ${response.status}`, baremes: [] };
      }
      const result = await response.json();
      return { success: true, baremes: result.data || [] };
    } catch (error) {
      return { success: false, error: error.message, baremes: [] };
    }
  }

  // 🔄 Mettre à jour le kilométrage utilisé annuel
  async updateKilometrageUtilise(inspecteurId, kmParcourus) {
    try {
      const baseUrl = this.getApiUrl();
      const token = this.getToken();
      
      if (!baseUrl || !token) {
        console.error('❌ Pas de token pour updateKilometrageUtilise');
        return { success: false, error: 'Token manquant' };
      }

      const url = `${baseUrl}/inspecteurs/${inspecteurId}`;
      console.log('🔄 MAJ kilométrage:', url, kmParcourus);
      
      const response = await fetch(url, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          vehicule_kilometrage_utilise_annuel: kmParcourus
        })
      });

      if (!response.ok) {
        console.error('❌ Erreur updateKilometrageUtilise:', response.status);
        return { success: false, error: `HTTP ${response.status}` };
      }

      const result = await response.json();
      console.log('✅ Kilométrage mis à jour:', result);
      return { success: true };
    } catch (error) {
      console.error('❌ Erreur updateKilometrageUtilise:', error);
      return { success: false, error: error.message };
    }
  }

  // ⏳ TODO: Créer note de frais kilométrique (à migrer)
  async createNoteDeFraisKilometrique(noteData) {
    return this.createNoteDeFrais({
      ...noteData,
      categorie: noteData.categorie || 'deplacement_vehicule',
    });
  }

  async listNotesDeFrais(params = {}) {
    try {
      const baseUrl = this.getApiUrl();
      const token = this.getToken();
      if (!baseUrl || !token) {
        return { success: false, error: 'Token manquant', notes: [] };
      }
      const query = new URLSearchParams(params).toString();
      const url = `${baseUrl}/notes-de-frais${query ? `?${query}` : ''}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });
      if (!response.ok) {
        return { success: false, error: `HTTP ${response.status}`, notes: [] };
      }
      const result = await response.json();
      return { success: true, notes: result.data || [] };
    } catch (error) {
      return { success: false, error: error.message, notes: [] };
    }
  }

  async updateNoteDeFrais(noteId, data) {
    try {
      const baseUrl = this.getApiUrl();
      const token = this.getToken();
      if (!baseUrl || !token) {
        return { success: false, error: 'Token manquant' };
      }
      const url = `${baseUrl}/notes-de-frais/${noteId}`;
      const response = await fetch(url, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        return { success: false, error: `HTTP ${response.status}` };
      }
      const result = await response.json();
      return { success: true, note: result.data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  async deleteNoteDeFrais(noteId) {
    try {
      const baseUrl = this.getApiUrl();
      const token = this.getToken();
      if (!baseUrl || !token) {
        return { success: false, error: 'Token manquant' };
      }
      const url = `${baseUrl}/notes-de-frais/${noteId}`;
      const response = await fetch(url, {
        method: 'DELETE',
        headers: this.getAuthHeaders(),
      });
      if (!response.ok) {
        return { success: false, error: `HTTP ${response.status}` };
      }
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }

  // 🏢 Récupérer les établissements d'un client
  async getClientEtablissements(clientId) {
    try {
      const baseUrl = this.getApiUrl();
      const token = this.getToken();
      
      if (!baseUrl || !token) {
        console.error('❌ Pas de token pour getClientEtablissements');
        return { success: false, error: 'Token manquant', etablissements: [] };
      }

      const url = `${baseUrl}/etablissements?clientId=${clientId}`;
      console.log('🏢 Chargement établissements:', url);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        console.error('❌ Erreur getClientEtablissements:', response.status);
        return { success: false, error: `HTTP ${response.status}`, etablissements: [] };
      }

      const result = await response.json();
      console.log('📦 Établissements chargés:', result.data?.length || 0);
      return { success: true, etablissements: result.data || [] };
    } catch (error) {
      console.error('❌ Erreur getClientEtablissements:', error);
      return { success: false, error: error.message, etablissements: [] };
    }
  }

  // ✅ Récupérer les paramètres entreprise
  async getParametres() {
    try {
      const baseUrl = this.getApiUrl();
      const token = this.getToken();
      if (!baseUrl || !token) {
        return { success: false, error: 'Token manquant', parametres: [] };
      }
      const url = `${baseUrl}/parametres`;
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getAuthHeaders(),
      });
      if (!response.ok) {
        return { success: false, error: `HTTP ${response.status}`, parametres: [] };
      }
      const result = await response.json();
      return { success: true, parametres: result.data || [] };
    } catch (error) {
      return { success: false, error: error.message, parametres: [] };
    }
  }

  /**
   * Récapitulatif mensuel de notes de frais (toutes catégories) au format PDF.
   * L'API filtre automatiquement sur l'inspecteur du token (pas besoin d'inspecteur_id côté mobile).
   * GET /api/notes-de-frais/recap-mensuel/pdf?mois_comptable=YYYY-MM
   */
  async downloadRecapMensuelPdf(moisComptable) {
    try {
      const baseUrl = this.getApiUrl();
      const token = this.getToken();
      if (!baseUrl || !token) {
        return { success: false, error: 'Non authentifié' };
      }
      const url = `${baseUrl}/notes-de-frais/recap-mensuel/pdf?mois_comptable=${encodeURIComponent(moisComptable)}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        let errMsg = `HTTP ${response.status}`;
        try {
          const errJson = await response.json();
          if (errJson.message) errMsg = errJson.message;
        } catch (_) { /* réponse non JSON (PDF attendu) */ }
        return { success: false, error: errMsg, status: response.status };
      }
      const blob = await response.blob();
      return { success: true, blob };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }
}

const apiService = new ApiService();
export default apiService;